import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExperimentFilters = ({ 
  activeCategory, 
  onCategoryChange, 
  activeStatus, 
  onStatusChange,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange
}) => {
  const categories = [
    { id: 'all', name: 'All Experiments', icon: 'Grid3X3', count: 12 },
    { id: 'webgl', name: 'WebGL Shaders', icon: 'Zap', count: 4 },
    { id: 'ai', name: 'AI Integration', icon: 'Brain', count: 3 },
    { id: 'interaction', name: 'Interactions', icon: 'MousePointer', count: 2 },
    { id: 'audio', name: 'Audio Visual', icon: 'Volume2', count: 2 },
    { id: 'emerging', name: 'Emerging Tech', icon: 'Rocket', count: 1 }
  ];

  const statusFilters = [
    { id: 'all', name: 'All Status', icon: 'Circle' },
    { id: 'active', name: 'Active', icon: 'CheckCircle' },
    { id: 'beta', name: 'Beta', icon: 'Clock' },
    { id: 'concept', name: 'Concept', icon: 'Lightbulb' }
  ];

  const sortOptions = [
    { id: 'recent', name: 'Most Recent', icon: 'Calendar' },
    { id: 'popular', name: 'Most Popular', icon: 'TrendingUp' },
    { id: 'complexity', name: 'Complexity', icon: 'BarChart3' },
    { id: 'alphabetical', name: 'A-Z', icon: 'ArrowUpDown' }
  ];

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Icon 
          name="Search" 
          size={20} 
          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
        />
        <input
          type="text"
          placeholder="Search experiments..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e?.target?.value)}
          className="w-full pl-12 pr-4 py-3 bg-surface/50 border border-border/20 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} />
          </button>
        )}
      </div>
      {/* Categories */}
      <div>
        <h3 className="font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
          <Icon name="Layers" size={18} className="text-accent" />
          <span>Categories</span>
        </h3>
        <div className="space-y-1">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => onCategoryChange(category?.id)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-all duration-200 ${
                activeCategory === category?.id
                  ? 'bg-accent/10 text-accent border border-accent/20' :'text-muted-foreground hover:text-foreground hover:bg-surface/30'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon name={category?.icon} size={16} />
                <span className="font-body font-medium">{category?.name}</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                activeCategory === category?.id
                  ? 'bg-accent/20 text-accent' :'bg-surface/50 text-muted-foreground'
              }`}>
                {category?.count}
              </span>
            </button>
          ))}
        </div>
      </div>
      {/* Status Filters */}
      <div>
        <h3 className="font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
          <Icon name="Filter" size={18} className="text-accent" />
          <span>Status</span>
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {statusFilters?.map((status) => (
            <button
              key={status?.id}
              onClick={() => onStatusChange(status?.id)}
              className={`flex items-center space-x-2 p-2 rounded-lg transition-all duration-200 ${
                activeStatus === status?.id
                  ? 'bg-accent/10 text-accent border border-accent/20' :'text-muted-foreground hover:text-foreground hover:bg-surface/30'
              }`}
            >
              <Icon name={status?.icon} size={14} />
              <span className="font-body text-sm">{status?.name}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Sort Options */}
      <div>
        <h3 className="font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
          <Icon name="ArrowUpDown" size={18} className="text-accent" />
          <span>Sort By</span>
        </h3>
        <div className="space-y-1">
          {sortOptions?.map((option) => (
            <button
              key={option?.id}
              onClick={() => onSortChange(option?.id)}
              className={`w-full flex items-center space-x-3 p-2 rounded-lg transition-all duration-200 ${
                sortBy === option?.id
                  ? 'bg-accent/10 text-accent' :'text-muted-foreground hover:text-foreground hover:bg-surface/30'
              }`}
            >
              <Icon name={option?.icon} size={14} />
              <span className="font-body text-sm">{option?.name}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="pt-4 border-t border-border/20">
        <h3 className="font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
          <Icon name="Zap" size={18} className="text-accent" />
          <span>Quick Actions</span>
        </h3>
        <div className="space-y-2">
          <Button
            variant="outline"
            fullWidth
            iconName="Shuffle"
            iconPosition="left"
            className="border-accent/30 text-accent hover:bg-accent/10"
          >
            Random Experiment
          </Button>
          <Button
            variant="ghost"
            fullWidth
            iconName="Bookmark"
            iconPosition="left"
            className="text-muted-foreground hover:text-foreground"
          >
            View Bookmarks
          </Button>
        </div>
      </div>
      {/* Community Suggestions */}
      <div className="pt-4 border-t border-border/20">
        <h3 className="font-headline font-bold text-foreground mb-3 flex items-center space-x-2">
          <Icon name="Users" size={18} className="text-cosmic-energy" />
          <span>Community</span>
        </h3>
        <div className="space-y-2">
          <Button
            variant="ghost"
            fullWidth
            iconName="MessageSquare"
            iconPosition="left"
            className="text-muted-foreground hover:text-cosmic-energy"
          >
            Suggest Experiment
          </Button>
          <Button
            variant="ghost"
            fullWidth
            iconName="Vote"
            iconPosition="left"
            className="text-muted-foreground hover:text-cosmic-energy"
          >
            Vote on Ideas
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExperimentFilters;